import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  reactionTypes,
  contentTypeConfigs,
  topReactedContent,
  reactionDashboardStats,
  getReactionById,
  getTopReactions,
  getTrendingReactions,
  getTrendingContent,
  type ReactionType,
  type ContentItem,
} from '@/mocks/rfwReactions';

function ReactionBubble({ reaction, size = 'md', showName = true }: { reaction: ReactionType; size?: 'sm' | 'md' | 'lg'; showName?: boolean }) {
  const sizeClasses = size === 'lg' ? 'w-20 h-20 text-2xl' : size === 'md' ? 'w-16 h-16 text-xl' : 'w-12 h-12 text-lg';
  const nameSize = size === 'lg' ? 'text-xs' : 'text-[10px]';
  return (
    <div className="flex flex-col items-center gap-1.5 group cursor-default">
      <div className={`${sizeClasses} rounded-full bg-gradient-to-r ${reaction.gradient} flex items-center justify-center shadow-sm ${reaction.glowColor} transition-transform duration-200 group-hover:scale-110`}>
        <span>{reaction.emoji}</span>
      </div>
      {showName && <span className={`${nameSize} font-bold ${reaction.textColor} text-center leading-tight`}>{reaction.shortName}</span>}
    </div>
  );
}

function ContentCard({ item }: { item: ContentItem }) {
  const mainReaction = getReactionById(item.mainReaction);
  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-foreground-300/50 transition-all duration-200 group">
      <div className="relative h-40 overflow-hidden">
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-3 left-3 right-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-white/20 backdrop-blur-sm text-white">
            <i className={`${contentTypeConfigs.find((ct) => ct.id === item.type)?.icon || 'ri-file-line'} text-[9px]`}></i>
            {contentTypeConfigs.find((ct) => ct.id === item.type)?.name}
          </span>
        </div>
        {item.trending && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[9px] font-bold">
            <i className="ri-fire-line text-[8px]"></i>TREND
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-sm font-semibold text-foreground-950 line-clamp-2 mb-2 leading-snug">{item.title}</p>
        <div className="flex items-center gap-2 mb-3">
          <img src={item.authorPhoto} alt={item.author} className="w-6 h-6 rounded-full object-cover object-top" />
          <span className="text-[10px] text-foreground-500 font-medium">{item.radio} · {item.timeAgo}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {mainReaction && (
              <span className="text-sm">{mainReaction.emoji}</span>
            )}
            <span className="text-sm font-bold text-foreground-950">{item.totalReactions.toLocaleString()}</span>
          </div>
          {mainReaction && (
            <span className={`text-[9px] font-semibold ${mainReaction.textColor}`}>
              {mainReaction.name} {item.mainReactionPct}%
            </span>
          )}
          <span className="text-[9px] text-foreground-400 ml-auto">{item.timeAgo}</span>
        </div>
      </div>
    </div>
  );
}

export default function ReaccionesSection() {
  const [activeTab, setActiveTab] = useState<'reacciones' | 'contenido' | 'tendencias'>('reacciones');
  const topReactions = getTopReactions(3);
  const trendingReactions = getTrendingReactions(3);
  const trendingContent = getTrendingContent(6);

  const tabs = [
    { id: 'reacciones' as const, label: 'Reacciones', icon: 'ri-emotion-line' },
    { id: 'contenido' as const, label: 'Contenido', icon: 'ri-article-line' },
    { id: 'tendencias' as const, label: 'Tendencias', icon: 'ri-line-chart-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE REACCIONES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Más Allá del Simple <span className="text-rose-600">Like</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            8 reacciones para expresar exactamente lo que sentís. Un solo toque. Aplican en posts, fotos, videos, comentarios, eventos, concursos, programas y locutores.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Reacciones', value: (reactionDashboardStats.totalReactions / 1000000).toFixed(1) + 'M', icon: 'ri-emotion-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Reacción Principal', value: reactionDashboardStats.mainReaction, sub: reactionDashboardStats.mainReactionCount.toLocaleString(), icon: 'ri-thumb-up-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'En Tendencia', value: reactionDashboardStats.trendingReaction, sub: `+${reactionDashboardStats.trendingReactionPercent}%`, icon: 'ri-fire-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Score Generado', value: (reactionDashboardStats.totalScoreGenerated / 1000000).toFixed(1) + 'M', icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Reactores Activos', value: (reactionDashboardStats.activeReactors / 1000).toFixed(0) + 'K', icon: 'ri-group-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
                {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'reacciones' && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {topReactions.map((r, i) => (
                <div key={r.id} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="text-[10px] font-bold text-foreground-400">#{i + 1}</div>
                  <ReactionBubble reaction={r} size="md" />
                  <span className="text-[10px] font-bold text-foreground-600">{(r.totalCount / 1000).toFixed(0)}K</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-3 md:gap-4">
              {reactionTypes.map((r) => (
                <ReactionBubble key={r.id} reaction={r} size="sm" />
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {contentTypeConfigs.map((ct) => (
                <div key={ct.id} className={`p-4 rounded-2xl ${ct.bgColor} border border-background-200/70`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${ct.icon} ${ct.color} text-base`}></i>
                    <span className={`text-xs font-bold ${ct.textColor}`}>{ct.name}</span>
                  </div>
                  <p className="text-[10px] text-foreground-500 mb-2">{ct.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {ct.applicableReactions.map((rid) => {
                      const r = getReactionById(rid);
                      if (!r) return null;
                      return <span key={rid} className="text-sm">{r.emoji}</span>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contenido' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {topReactedContent.slice(0, 6).map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {activeTab === 'tendencias' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {trendingReactions.map((r) => (
                <div key={r.id} className="flex items-center gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="shrink-0">
                    <ReactionBubble reaction={r} size="md" showName={false} />
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${r.textColor}`}>{r.name}</p>
                    <p className="text-xs text-foreground-500">{r.description}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs font-bold text-emerald-600">+{r.trendPercent}%</span>
                      <span className="text-[10px] text-foreground-400">{r.totalCount.toLocaleString()} total</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {trendingContent.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/reacciones"
            className="inline-flex items-center gap-2 px-6 py-3 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-emotion-line"></i>
            Explorar Todas las Reacciones
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-hand-heart-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Una reacción debe requerir un solo toque.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Sin menús, sin confirmaciones, sin pasos extra. Tocás el emoji y listo — la reacción queda registrada al instante. Simple, rápido y divertido. Así se mantiene la interacción fluida y natural en toda la plataforma.
          </p>
        </div>
      </div>
    </section>
  );
}